import {
	ButtonWithLoading,
	HelmetDexteel,
	MesfModal,
	TimeService,
	getMomentTz,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Button,
	Checkbox,
	FormControlLabel,
	Grid2 as Grid,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LazyLoading } from "../../../../controls/LazyLoading";
import { Process } from "../../../caster/models/ProcessGanttChart";
import { upsertProcess as upsertCasterProcess } from "../../../caster/repositories/ProductionRepository";
import { upsertProcess as upsertEAFProcess } from "../../../eaf/eaf-processes/repositories/EAFRepository";
import { useCasterDashboardContext } from "../../CasterDashboardContext";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	refreshData: Function;
	comparisonDate: Date | null;
};

export const UpsertProcess = ({
	show,
	onHide,
	refreshData,
	comparisonDate,
}: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isNew, setIsNew] = useState(false);
	const [isNotClosed, setIsNotClosed] = useState(false);
	const [title, setTitle] = useState("");
	const [helmet, setHelmet] = useState("");
	const { state, actions } = useCasterDashboardContext();
	const [warning, setWarning] = useState(false);
	const [endStartWarning, setEndStartWarning] = useState(false);
	const { shiftInfo } = useShiftNavigator();
	const [previousProcess, setPreviousProcess] = useState<Process | null>(null);
	const [nextProcess, setNextProcess] = useState<Process | null>(null);
	const [processCreationErrorMessage, setProcessCreationErrorMessage] =
		useState<string>("");

	const isCaster =
		state.selectedAsset?.id && Number(state.selectedAsset?.id) == 13;

	const startLabel = isCaster ? "Ladle Open" : "Start";
	const endLabel = isCaster ? "Ladle Close" : "End";

	const findPreviousAndNextProcess = (
		processes: Process[],
		timestamp: Date,
		currentProcessId?: number,
	) => {
		let previousProcess: Process | null = null;
		let nextProcess: Process | null = null;
		for (const process of processes) {
			if (currentProcessId && process.ProcessId === currentProcessId) {
				continue;
			}
			if (process.End && process.End <= timestamp) {
				if (!previousProcess || process.End > previousProcess.End) {
					previousProcess = process;
				}
			}
		}
		const baseTime = previousProcess ? previousProcess.End : timestamp;

		for (const process of processes) {
			if (currentProcessId && process.ProcessId === currentProcessId) {
				continue;
			}
			if (process.Start > baseTime) {
				if (!nextProcess || process.Start < nextProcess.Start) {
					nextProcess = process;
				}
			}
		}

		return { previousProcess, nextProcess };
	};

	const {
		setValue,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<Process>({
		defaultValues: {},
		mode: "onChange",
	});

	const CurrentStart = get(shiftInfo, "CurrentStart", null);
	const CurrentShiftEnd = get(shiftInfo, "CurrentEnd", null);

	const handleDateChange = (fieldOnChange: any) => (date: any) => {
		const currentStart = moment.utc(CurrentStart).local();
		const dateToCompare = moment.utc(date).local();

		fieldOnChange(date);
		if (!dateToCompare.isSame(currentStart)) {
			setWarning(true);
		} else {
			setWarning(false);
		}
	};

	const onSubmit: SubmitHandler<Process> = async (data: Process) => {
		setIsSubmitLoading(true);
		const start = data.Start;
		const end = data.End;
		const { selectedProcess, selectedAsset } = state;
		const processId = data.ProcessId === 0 ? null : data.ProcessId;

		const assetId =
			selectedProcess && selectedProcess.ProcessId !== 0
				? selectedProcess.AssetId
				: Number(selectedAsset?.id);

		if (!assetId) {
			actions.setErrorMessage("Asset ID is not defined.");
			setIsSubmitLoading(false);
			return;
		}

		const isCaster = assetId === 13;

		const response = isCaster
			? await upsertCasterProcess(processId, start, end)
			: await upsertEAFProcess(processId, start, end, true, assetId);

		if (response.ok) {
			actions.setNotificationMessage(
				`Process was ${isNew ? "created" : "edited"} successfully`,
			);
			await refreshData();
		} else {
			actions.setErrorMessage(response?.message);
		}
		setIsSubmitLoading(false);
		onHide(true);
	};

	const open = watch("Start");
	const close = watch("End");

	useEffect(() => {
		if (Math.abs(moment(open).diff(moment(close), "hours")) > 3) {
			setEndStartWarning(true);
		} else {
			setEndStartWarning(false);
		}
	}, [open, close]);

	useEffect(() => {
		setWarning(false);
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				setProcessCreationErrorMessage("");
				const process = state.selectedProcess;
				if (process === undefined || process === null) return;
				if (process.ProcessId === 0) {
					// new process
					setTitle(`NEW
						${state.selectedAsset?.name.toUpperCase()}
						 PROCESS`);
					setHelmet(`New
						${state.selectedAsset?.name.toUpperCase()}
						 Process`);
					setIsNew(true);

					if (state.selectedAsset && comparisonDate) {
						const filteredProcesses = state.ganttData.Processes.filter(
							(p) => p.AssetId === Number(state.selectedAsset?.id),
						);
						const { previousProcess, nextProcess } = findPreviousAndNextProcess(
							filteredProcesses,
							comparisonDate,
						);
						setPreviousProcess(previousProcess);
						setNextProcess(nextProcess);
						let startTime;
						let endTime;
						if (previousProcess && previousProcess.End) {
							startTime = moment(previousProcess.End)
								.add(2, "minutes")
								.toDate();
						} else {
							startTime = CurrentStart ? CurrentStart : moment().toDate();
						}

						if (nextProcess && nextProcess.ProcessStart) {
							endTime = moment(nextProcess.ProcessStart)
								.subtract(2, "minutes")
								.toDate();
						} else {
							endTime = CurrentShiftEnd
								? moment(CurrentShiftEnd).add(1, "hour").toDate()
								: moment().toDate();
						}

						if (
							previousProcess &&
							previousProcess.End &&
							nextProcess &&
							nextProcess.ProcessStart
						) {
							const timeGap = moment(nextProcess.ProcessStart).diff(
								moment(previousProcess.End),
								"minutes",
							);

							if (timeGap < 4) {
								setProcessCreationErrorMessage(
									"The space between processes is too small to create a new process in the selected position.",
								);
								setValue("Start", previousProcess.End);
								setValue("End", nextProcess.ProcessStart);
								setIsLoading(false);
								return;
							}
						}

						setValue("ProcessId", 0);
						setValue("Start", startTime);
						setValue("End", endTime);
					}
				} else {
					// edit process
					setTitle(
						`EDIT 
							${state.selectedProcess?.AssetId == 13 ? "CASTER" : "EAF"}
							PROCESS`,
					);
					setHelmet(`Edit
						${state.selectedProcess?.AssetId == 13 ? "CASTER" : "EAF"}
						 Process`);
					setIsNew(false);

					// Set previousProcess and nextProcess for validation
					if (process.ProcessStart) {
						// Find processes for the same asset from ganttData
						const filteredProcesses = state.ganttData.Processes.filter(
							(p) => p.AssetId === process.AssetId,
						);
						const { previousProcess, nextProcess } = findPreviousAndNextProcess(
							filteredProcesses,
							process.ProcessStart,
							process.ProcessId,
						);
						setPreviousProcess(previousProcess);
						setNextProcess(nextProcess);
					}

					setValue("ProcessId", process.ProcessId);
					setValue("Start", process.ProcessStart);
					setValue("End", process.End);
				}
				if (process.End === null) setIsNotClosed(true);
				setIsLoading(false);
			})();
		} else {
			reset();
			setIsNotClosed(false);
		}
	}, [show]);

	useEffect(() => {
		if (isNotClosed) {
			setValue("End", null as any);
		}
	}, [isNotClosed]);

	return (
		<>
			{show && <HelmetDexteel title={`${helmet}`} />}
			<Grid container>
				<Grid size={{ xs: 12 }}>
					<MesfModal
						maxWidth="md"
						open={show}
						handleClose={() => {
							onHide(true);
						}}
						id="upsertProcess"
						title={title}
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px" }}>
								{isLoading && (
									<Grid
										container
										justifyContent="center"
										alignItems="center"
										style={{ minHeight: "300px" }}
									>
										<LazyLoading />
									</Grid>
								)}
								{!isLoading && (
									<>
										<Grid container spacing={1}>
											<Grid size={{ xs: 12, md: 6 }}>
												<Controller
													name="Start"
													control={control}
													rules={{
														required: "The Start field is required",
														validate: {
															notAfterEnd: (value) => {
																const endValue = watch("End");
																if (!endValue) return true;
																return (
																	moment(value).isBefore(moment(endValue)) ||
																	"Start must be before End"
																);
															},
															notBeforePreviousEnd: (value) => {
																const start = previousProcess?.End;
																if (!start) return true;
																return (
																	moment(value).isSameOrAfter(moment(start)) ||
																	"Start cannot be less than the End of the Previous Process"
																);
															},
															notAfterNextStart: (value) => {
																if (!nextProcess?.Start) return true;
																return (
																	moment(value).isSameOrBefore(
																		moment(nextProcess.Start),
																	) ||
																	"Start cannot be greater than the Start of the Next Process"
																);
															},
														},
													}}
													render={({ field }) => (
														<DateTimePicker
															label={startLabel}
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YYYY HH:mm"
															disabled={!!processCreationErrorMessage}
															timeSteps={{ minutes: 1 }}
															value={moment(field.value) as any}
															onChange={handleDateChange(field.onChange)}
															inputRef={field.ref}
															slotProps={{
																textField: {
																	variant: "outlined",
																	error: !!errors.Start,
																},
															}}
														/>
													)}
												/>
												{errors.Start && (
													<p style={{ color: "red", fontSize: 12 }}>
														{errors.Start?.message}
													</p>
												)}
											</Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid size={{ xs: 12, md: 6 }} style={{ marginTop: 10 }}>
												<Controller
													name="End"
													control={control}
													rules={{
														required: !isNotClosed
															? "The End field is required"
															: false,
														validate: {
															notBeforeStart: (value) => {
																const startValue = watch("Start");
																if (!startValue) return true;
																return (
																	moment(value).isAfter(moment(startValue)) ||
																	isNotClosed ||
																	"End must be after Start"
																);
															},
															notAfterNextStart: (value) => {
																if (!nextProcess?.ProcessStart) return true;
																return (
																	moment(value).isSameOrBefore(
																		moment(nextProcess.ProcessStart),
																	) ||
																	"End cannot be greater than the Start of the Next Process"
																);
															},
														},
													}}
													render={({ field }) => (
														<DateTimePicker
															label={endLabel}
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YYYY HH:mm"
															disabled={
																!!processCreationErrorMessage || isNotClosed
															}
															timeSteps={{ minutes: 1 }}
															value={moment(field.value) as any}
															onChange={field.onChange}
															inputRef={field.ref}
															slotProps={{
																textField: {
																	variant: "outlined",
																	error: !!errors.End,
																},
															}}
														/>
													)}
												/>
												{errors.End && (
													<p style={{ color: "red", fontSize: 12 }}>
														{errors.End?.message}
													</p>
												)}
											</Grid>
											{processCreationErrorMessage && (
												<p style={{ color: "red", fontSize: 18 }}>
													{processCreationErrorMessage}
												</p>
											)}
											<Grid size={{ xs: 12, md: 6 }} style={{ marginTop: 20 }}>
												<FormControlLabel
													control={
														<Checkbox
															color="primary"
															checked={isNotClosed}
															onChange={(event) => {
																setIsNotClosed(event.target.checked);
																if (event.target.checked) {
																	setValue("End", null as any);
																} else {
																	setValue("End", new Date());
																}
															}}
														/>
													}
													label="Process not finished"
												/>
											</Grid>
										</Grid>
									</>
								)}
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide(true)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
										{!!processCreationErrorMessage ? (
											<Button
												fullWidth
												variant="contained"
												color="primary"
												type="submit"
												disabled={!!processCreationErrorMessage}
											>
												Save
											</Button>
										) : (
											<ButtonWithLoading
												fullWidth
												variant="contained"
												color="primary"
												type="submit"
												isLoading={isSubmitLoading}
											>
												Save
											</ButtonWithLoading>
										)}
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
		</>
	);
};
